/** Utilities for determining project states based on next actions and hierarchy. */

/**
 * Traverses up the ancestor chain of a task and adds all ancestor IDs to a given Set.
 *
 * @param {import('@/types').Task} startTask - The task to start from.
 * @param {Set<string>} stateSet - The set to add task IDs to.
 * @param {Map<string, import('@/types').Task>} tasksMap - Map of all tasks by ID.
 */
export const cascadeStateUp = (startTask, stateSet, tasksMap) => {
  let currentTask = tasksMap.get(startTask.id);
  while (currentTask) {
    stateSet.add(currentTask.id);
    if (currentTask.parentId) {
      currentTask = tasksMap.get(currentTask.parentId);
    } else {
      currentTask = null; // Reached the top of this branch
    }
  }
};

/**
 * Calculates the sets of planned and waiting project IDs.
 *
 * @param {Map<string, import('@/types').Task>} tasksMap - Map of all tasks.
 * @param {Map<string, {nextAction: import('@/types').Task, state: string}>} nextActions - Map of next actions by root task ID.
 * @returns {{plannedParentIds: Set<string>, waitingForParentIds: Set<string>}}
 */
export const calculateProjectStates = (tasksMap, nextActions) => {
  const plannedParentIds = new Set();
  const waitingForParentIds = new Set();

  for (const [, { nextAction, state }] of nextActions.entries()) {
    if (!nextAction) continue;

    if (state === 'planned') {
      cascadeStateUp(nextAction, plannedParentIds, tasksMap);
    }

    if (state === 'waiting') {
      cascadeStateUp(nextAction, waitingForParentIds, tasksMap);
    }
  }

  return { plannedParentIds, waitingForParentIds };
};
